/**
 * Device position.
 *
 * Deliberately conservative about what it reports. A navigation display that
 * shows a stale or low-quality fix as though it were good is worse than one that
 * admits it does not know where it is, so fix age and accuracy are surfaced
 * rather than hidden.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Fix } from '../core/boat';

export type GeolocationStatus =
  | 'idle'
  | 'requesting'
  | 'active'
  | 'denied'
  | 'unavailable'
  | 'error';

export interface GeolocationState {
  status: GeolocationStatus;
  fix: Fix | null;
  /** Human-readable, written for the skipper rather than the developer. */
  message: string | null;
}

/**
 * A fix older than this is shown as stale. Thirty seconds is long enough to ride
 * out a brief signal loss and short enough that a frozen position is noticed
 * before it matters.
 */
export const STALE_FIX_MS = 30_000;

/** Accuracy worse than this is called out; typical open-sky GPS is 5-15 m. */
export const POOR_ACCURACY_M = 50;

export function isStale(fix: Fix | null, now = Date.now()): boolean {
  return fix !== null && now - fix.timestamp > STALE_FIX_MS;
}

export function isPoorAccuracy(fix: Fix | null): boolean {
  return fix !== null && fix.accuracyM > POOR_ACCURACY_M;
}

function toFix(position: GeolocationPosition): Fix {
  const { coords } = position;
  return {
    lat: coords.latitude,
    lon: coords.longitude,
    accuracyM: Number.isFinite(coords.accuracy) ? coords.accuracy : Number.POSITIVE_INFINITY,
    // The Geolocation API reports heading only while moving, and NaN otherwise.
    courseDeg: coords.heading !== null && Number.isFinite(coords.heading) ? coords.heading : null,
    speedMs: coords.speed !== null && Number.isFinite(coords.speed) ? coords.speed : null,
    timestamp: position.timestamp,
  };
}

export function useGeolocation(enabled: boolean): GeolocationState & { retry: () => void } {
  const [state, setState] = useState<GeolocationState>({ status: 'idle', fix: null, message: null });
  const [attempt, setAttempt] = useState(0);
  const watchId = useRef<number | null>(null);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({ ...prev, status: 'idle' }));
      return undefined;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({
        status: 'unavailable',
        fix: null,
        message: 'This device or browser does not provide a position.',
      });
      return undefined;
    }

    setState((prev) => ({ ...prev, status: 'requesting', message: null }));

    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        setState({ status: 'active', fix: toFix(position), message: null });
      },
      (error) => {
        // Keep the last known fix on a transient failure — a boat that briefly
        // loses signal has not teleported, and blanking the display mid-passage
        // is worse than showing a position marked stale.
        setState((prev) => {
          if (error.code === error.PERMISSION_DENIED) {
            return {
              status: 'denied',
              fix: null,
              message: 'Location permission was refused. Grant it in your browser settings to plot your boat.',
            };
          }
          if (error.code === error.POSITION_UNAVAILABLE) {
            return {
              status: 'error',
              fix: prev.fix,
              message: 'No position available. This is common below decks or between buildings.',
            };
          }
          return {
            status: 'error',
            fix: prev.fix,
            message: 'Still searching for a position fix.',
          };
        });
      },
      {
        enableHighAccuracy: true,
        // A navigation display must never be handed a cached fix as if it were
        // current; always take a fresh one.
        maximumAge: 0,
        timeout: 20_000,
      },
    );

    return () => {
      if (watchId.current !== null) {
        navigator.geolocation.clearWatch(watchId.current);
        watchId.current = null;
      }
    };
  }, [enabled, attempt]);

  return { ...state, retry };
}

/**
 * Keep the screen awake while navigating.
 *
 * A phone that sleeps mid-passage is useless, and the Wake Lock API drops the
 * lock whenever the page is hidden, so it must be reacquired on visibility
 * change rather than taken once.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return undefined;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        sentinel = await navigator.wakeLock.request('screen');
      } catch {
        // Denied or unsupported: not worth interrupting the skipper over.
      }
    };

    const onVisibilityChange = (): void => {
      if (!cancelled && document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void sentinel?.release().catch(() => {});
    };
  }, [enabled]);
}
